import { $getSelection } from 'lexical';
import { $patchStyleText } from '@lexical/selection';
import { useState, useEffect } from 'react';
import { Remove, Add } from '@mui/icons-material';
import { IconButton, Tooltip, Box } from '@mui/material';

const MIN_ALLOWED_FONT_SIZE = 8;
const MAX_ALLOWED_FONT_SIZE = 72;
const DEFAULT_FONT_SIZE = 15;

const THEME = {
  primary: '#232536',
  iconDefault: '#4b5563',
  toolbarBorder: '#e2e5ee',
  hoverBg: 'rgba(35, 37, 54, 0.07)',
};

export const FONT_SIZE_OPTIONS = [
  ['10px', '10px'],
  ['11px', '11px'],
  ['12px', '12px'],
  ['13px', '13px'],
  ['14px', '14px'],
  ['15px', '15px'],
  ['16px', '16px'],
  ['17px', '17px'],
  ['18px', '18px'],
  ['19px', '19px'],
  ['20px', '20px'],
];

export function parseAllowedFontSize(input) {
  const match = input.match(/^(\d+(?:\.\d+)?)px$/);
  if (match) {
    const n = Number(match[1]);
    if (n >= MIN_ALLOWED_FONT_SIZE && n <= MAX_ALLOWED_FONT_SIZE) {
      return input;
    }
  }
  return '';
}

function updateFontSizeInSelection(editor, newFontSize, updateType) {
  editor.update(() => {
    if (editor.isComposing()) return;
    const selection = $getSelection();
    if (selection !== null) {
      $patchStyleText(selection, { 'font-size': newFontSize || null });
    }
  });
}

export function FontSize({ selectionFontSize, disabled, editor }) {
  const [inputValue, setInputValue] = useState(selectionFontSize);
  const [inputChangeFlag, setInputChangeFlag] = useState(false);

  useEffect(() => {
    if (!inputChangeFlag) {
      setInputValue(selectionFontSize);
    }
  }, [selectionFontSize, inputChangeFlag]);

  const handleButtonClick = (direction) => {
    const currentSize = parseInt(inputValue) || DEFAULT_FONT_SIZE;
    let newSize;
    if (direction === 'increment') {
      newSize = Math.min(currentSize + 1, MAX_ALLOWED_FONT_SIZE);
    } else {
      newSize = Math.max(currentSize - 1, MIN_ALLOWED_FONT_SIZE);
    }
    setInputValue(`${newSize}px`);
    updateFontSizeInSelection(editor, `${newSize}px`);
  };

  const handleKeyPress = (e) => {
    if (['e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault();
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      let val = parseInt(inputValue);
      if (isNaN(val)) val = DEFAULT_FONT_SIZE;
      val = Math.max(MIN_ALLOWED_FONT_SIZE, Math.min(MAX_ALLOWED_FONT_SIZE, val));
      setInputValue(`${val}px`);
      updateFontSizeInSelection(editor, `${val}px`);
      setInputChangeFlag(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      <Tooltip title="Decrease font size" arrow enterDelay={400}>
        <span>
          <IconButton
            size="small"
            disabled={disabled || parseInt(inputValue) <= MIN_ALLOWED_FONT_SIZE}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleButtonClick('decrement')}
            sx={{
              width: 26,
              height: 28,
              borderRadius: '4px 0 0 4px',
              border: `1px solid ${THEME.toolbarBorder}`,
              borderRight: 'none',
              color: THEME.iconDefault,
              transition: 'all 0.15s',
              '&:hover': { backgroundColor: THEME.hoverBg },
              '&.Mui-disabled': { opacity: 0.4 },
            }}
          >
            <Remove sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <input
        type="text"
        inputMode="numeric"
        value={inputValue.replace('px', '')}
        disabled={disabled}
        min={MIN_ALLOWED_FONT_SIZE}
        max={MAX_ALLOWED_FONT_SIZE}
        onChange={(e) => {
          const numericValue = e.target.value.replace(/[^0-9]/g, '');
          setInputValue(`${numericValue}px`);
          setInputChangeFlag(true);
        }}
        onKeyDown={handleKeyPress}
        onBlur={() => setInputChangeFlag(false)}
        style={{
          width: 36,
          height: 28,
          textAlign: 'center',
          fontSize: '0.8125rem',
          fontWeight: 500,
          border: `1px solid ${THEME.toolbarBorder}`,
          borderLeft: 'none',
          borderRight: 'none',
          outline: 'none',
          fontFamily: 'inherit',
          color: THEME.primary,
          backgroundColor: '#fff',
          padding: 0,
          boxSizing: 'border-box',
          MozAppearance: 'textfield',
        }}
      />
      <Tooltip title="Increase font size" arrow enterDelay={400}>
        <span>
          <IconButton
            size="small"
            disabled={disabled || parseInt(inputValue) >= MAX_ALLOWED_FONT_SIZE}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleButtonClick('increment')}
            sx={{
              width: 26,
              height: 28,
              borderRadius: '0 4px 4px 0',
              border: `1px solid ${THEME.toolbarBorder}`,
              borderLeft: 'none',
              color: THEME.iconDefault,
              transition: 'all 0.15s',
              '&:hover': { backgroundColor: THEME.hoverBg },
              '&.Mui-disabled': { opacity: 0.4 },
            }}
          >
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
